import moment from "moment/moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { FiBell, FiCalendar, FiClock, FiSearch, FiZap } from "react-icons/fi";
import Layout from "../../../components/layouts/Layout";
import {
  Table,
  TableBody,
  TableHeader,
  TablePagination,
} from "../../../components/table";
import {
  asArray,
  fetchAutomationFlows,
  fetchSmsBillingOverview,
  fetchSmsConversations,
  normaliseCollection,
  normaliseSmsNotificationsResponse,
  normaliseSmsNotificationSummary,
  readFirstDefined,
  smsNotificationActionsAvailable,
  smsReminderActionsAvailable,
  SMS_NOTIFICATION_TYPE_LABELS,
} from "../AutomationTemplates/automationTemplateApi";
import SmsAccessGuard from "../Components/SmsAccessGuard";
import "../style/sms-billing.css";

const DEFAULT_PAGE_SIZE = 5;

const NOTIFICATION_FILTERS = [
  { label: "All Types", value: "" },
  { label: "Unread", value: "unread" },
  { label: "Customer Replies", value: "customer_reply" },
  { label: "Reminders", value: "reminder" },
  { label: "Automation", value: "automation" },
  { label: "Delivery", value: "delivery_report" },
  { label: "Credit Alerts", value: "credit_warning" },
];

const formatDateTime = (date) => {
  if (!date) {
    return "Not available";
  }

  const parsedDate = moment(date);

  return parsedDate.isValid() ? parsedDate.format("MMM DD, YYYY h:mm A") : "Not available";
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "0";
  }

  const numberValue = Number(value);

  return Number.isNaN(numberValue) ? "0" : numberValue.toLocaleString();
};

const normaliseBillingSummary = (payload) => {
  const summary = normaliseSmsNotificationSummary(payload);
  const source = payload?.data || payload || {};
  const summarySource =
    source.summary ||
    source.notifications ||
    source.notification_summary ||
    source.notificationSummary ||
    source.overview ||
    source;
  const summaryRecord = Array.isArray(summarySource) ? summarySource[0] || {} : summarySource;

  return {
    unreadNotifications: summary.unreadNotifications,
    pendingReminders: readFirstDefined(summaryRecord, [
      "pending_reminders",
      "pendingReminders",
      "scheduled_reminders",
      "scheduledReminders",
    ]),
    todayNotifications: readFirstDefined(summaryRecord, [
      "today_notifications",
      "todayNotifications",
      "notifications_today",
      "notificationsToday",
    ]),
    automationAlerts: readFirstDefined(summaryRecord, [
      "automation_alerts",
      "automationAlerts",
      "failed_notifications",
      "failedNotifications",
      "notification_failures",
    ]),
  };
};

const readFlowReminderActions = (flow) =>
  readFirstDefined(flow, ["reminder_actions", "reminderActions", "reminders"]) || [];

const normaliseReminderDefinitions = (payload) => {
  const collection = normaliseCollection(payload, ["flows", "automation_flows", "automationFlows"]);
  const reminders = collection.data.flatMap((flow, flowIndex) => {
    const actions = asArray(readFlowReminderActions(flow));
    const flowName = readFirstDefined(flow, ["name", "flow_name", "flowName", "title"]) || "Automation Flow";

    return actions.map((action, actionIndex) => ({
      id:
        readFirstDefined(action, ["id", "reminder_id", "reminderId", "uuid"]) ||
        `flow-${flowIndex}-reminder-${actionIndex}`,
      title:
        readFirstDefined(action, ["title", "name", "label", "message"]) ||
        `Reminder from ${flowName}`,
      customerName: readFirstDefined(action, ["customer_name", "customerName"]),
      eventName: readFirstDefined(action, ["event_name", "eventName"]),
      dueAt: readFirstDefined(action, ["due_at", "dueAt", "scheduled_at", "scheduledAt", "send_at", "sendAt"]),
      status: readFirstDefined(action, ["status", "state"]) || "Configured",
      flowName,
    }));
  });

  return {
    data: reminders,
    currentPage: 1,
    total: reminders.length,
    perPage: reminders.length || DEFAULT_PAGE_SIZE,
    from: reminders.length ? 1 : 0,
    to: reminders.length,
  };
};

const NotificationBadge = ({ type }) => (
  <span className={`badge notification-type-badge type-${type || "system"}`}>
    {SMS_NOTIFICATION_TYPE_LABELS[type] || SMS_NOTIFICATION_TYPE_LABELS.system}
  </span>
);

const ReadBadge = ({ unread }) => (
  <span className={`notification-read-chip ${unread ? "unread" : "read"}`}>
    {unread ? "Unread" : "Read"}
  </span>
);

const DetailRow = ({ label, value }) => (
  <>
    <dt>{label}</dt>
    <dd>{value || "Not available"}</dd>
  </>
);

const Notifications = () => {
  const [summary, setSummary] = useState({
    unreadNotifications: null,
    pendingReminders: null,
    todayNotifications: null,
    automationAlerts: null,
  });
  const [notifications, setNotifications] = useState({
    data: [],
    currentPage: 1,
    total: 0,
    perPage: DEFAULT_PAGE_SIZE,
    from: 0,
    to: 0,
  });
  const [reminders, setReminders] = useState({
    data: [],
    currentPage: 1,
    total: 0,
    perPage: DEFAULT_PAGE_SIZE,
    from: 0,
    to: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeyWord, setSearchKeyWord] = useState("");
  const [searchKeyWordDebounce] = useDebounce(searchKeyWord, 600);
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const loadSummary = useCallback(async (isMounted = () => true) => {
    try {
      const response = await fetchSmsBillingOverview();

      if (isMounted()) {
        setSummary(normaliseBillingSummary(response.data));
      }
    } catch (error) {
      if (isMounted()) {
        setSummary({
          unreadNotifications: null,
          pendingReminders: null,
          todayNotifications: null,
          automationAlerts: null,
        });
      }
    }
  }, []);

  const loadNotifications = useCallback(async (isMounted = () => true) => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: DEFAULT_PAGE_SIZE,
        search: searchKeyWordDebounce,
      };

      if (typeFilter === "unread") {
        params.unread = 1;
      } else if (typeFilter) {
        params.type = typeFilter;
      }

      const response = await fetchSmsConversations(params);

      if (isMounted()) {
        const nextNotifications = normaliseSmsNotificationsResponse(response.data);
        const filteredData = nextNotifications.data.filter((item) => {
          if (typeFilter === "unread") {
            return item.unread;
          }

          return typeFilter ? item.type === typeFilter : true;
        });

        setNotifications({
          ...nextNotifications,
          data: filteredData,
          total: typeFilter ? filteredData.length : nextNotifications.total,
          from: typeFilter && filteredData.length ? 1 : nextNotifications.from,
          to: typeFilter ? filteredData.length : nextNotifications.to,
        });
        setApiAvailable(true);
      }
    } catch (error) {
      if (isMounted()) {
        setNotifications({
          data: [],
          currentPage: 1,
          total: 0,
          perPage: DEFAULT_PAGE_SIZE,
          from: 0,
          to: 0,
        });
        setApiAvailable(false);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [currentPage, searchKeyWordDebounce, typeFilter]);

  const loadReminders = useCallback(async (isMounted = () => true) => {
    try {
      setRemindersLoading(true);
      const response = await fetchAutomationFlows({ page: 1 });

      if (isMounted()) {
        setReminders(normaliseReminderDefinitions(response.data));
      }
    } catch (error) {
      if (isMounted()) {
        setReminders({
          data: [],
          currentPage: 1,
          total: 0,
          perPage: DEFAULT_PAGE_SIZE,
          from: 0,
          to: 0,
        });
      }
    } finally {
      if (isMounted()) {
        setRemindersLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    loadSummary(() => mounted);
    loadNotifications(() => mounted);
    loadReminders(() => mounted);

    return () => {
      mounted = false;
    };
  }, [loadSummary, loadNotifications, loadReminders]);

  const derivedSummary = useMemo(() => {
    const unreadNotifications = notifications.data.filter((item) => item.unread).length;
    const todayNotifications = notifications.data.filter((item) =>
      item.timestamp ? moment(item.timestamp).isSame(moment(), "day") : false
    ).length;

    return {
      unreadNotifications: summary.unreadNotifications ?? unreadNotifications,
      pendingReminders: summary.pendingReminders ?? reminders.data.length,
      todayNotifications: summary.todayNotifications ?? todayNotifications,
      automationAlerts: summary.automationAlerts ?? 0,
    };
  }, [notifications.data, reminders.data.length, summary]);

  const handleSearch = (query) => {
    setSearchKeyWord(query);
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
    setCurrentPage(1);
  };

  const closeDetail = () => {
    setSelectedNotification(null);
  };

  const tabs = [
    ["Overview", "/conversions"],
    ["Conversations", "/conversions/conversations"],
    ["Notifications", "/conversions/notifications", true],
    ["Analytics", "/conversions/analytics"],
    ["My Automation Flows", "/conversions/automation-flows"],
    ["Browse Templates", "/conversions/sms-automation-templates"],
    ["Billing", "/conversions/billing"],
  ];
  const summaryCards = [
    { label: "Unread Notifications", value: derivedSummary.unreadNotifications, detail: "Needs your attention", icon: <FiBell />, tone: "blue" },
    { label: "Pending Reminders", value: derivedSummary.pendingReminders, detail: "Waiting to be sent", icon: <FiClock />, tone: "gold" },
    { label: "Today's Notifications", value: derivedSummary.todayNotifications, detail: "Since midnight", icon: <FiCalendar />, tone: "green" },
    { label: "Automation Alerts", value: derivedSummary.automationAlerts, detail: "System notifications", icon: <FiZap />, tone: "violet" },
  ];

  return (
    <SmsAccessGuard>
      <Layout>
        <main className="page notification-dashboard-page">
          <header className="notification-page-title">
            <h1>Notifications</h1>
            <p>Monitor customer replies, automation events, reminders, and system alerts.</p>
          </header>

          <section className="notification-summary-grid">
            {summaryCards.map((card) => (
              <article className="notification-dashboard-card" key={card.label}>
                <span className={`notification-dashboard-icon ${card.tone}`}>{card.icon}</span>
                <div><span>{card.label}</span><strong>{formatNumber(card.value)}</strong><small>{card.detail}</small></div>
              </article>
            ))}
          </section>

          <nav className="conversations-tabs notification-dashboard-tabs" aria-label="Conversations sections">
            {tabs.map(([label, path, active]) => <Link key={path} to={path} className={active ? "active" : ""}>{label}</Link>)}
          </nav>

          <section className="notification-list-card">
            <header className="notification-card-header">
              <h2>Notification List</h2>
              <button type="button" disabled={!smsNotificationActionsAvailable}>Mark All As Read</button>
            </header>
            <div className="notification-list-filters">
              <label><input value={searchKeyWord} onChange={(event) => handleSearch(event.target.value)} placeholder="Search notifications" aria-label="Search notifications" /><FiSearch /></label>
              <select value={typeFilter} onChange={handleTypeFilterChange} aria-label="Notification type">
                {NOTIFICATION_FILTERS.map((filter) => <option key={filter.label} value={filter.value}>{filter.label}</option>)}
              </select>
            </div>
            <div className="notification-table-wrap">
              <Table>
                <TableHeader><tr><th>Notification</th><th>Type</th><th>Event</th><th>Customer</th><th>Time</th><th>Status</th><th>Actions</th></tr></TableHeader>
                <TableBody>
                  {loading ? <tr><td colSpan={7}><div className="notification-table-state"><span className="spinner-border" /></div></td></tr> : null}
                  {!loading && notifications.data.length ? notifications.data.map((notification) => (
                    <tr key={notification.id}>
                      <td><strong>{notification.title || "Notification"}</strong><p className="notification-message-preview mb-0">{notification.message || "No message available."}</p></td>
                      <td><NotificationBadge type={notification.type} /></td>
                      <td>{notification.eventName || "Not available"}</td>
                      <td>{notification.customerName || "Not available"}</td>
                      <td>{formatDateTime(notification.timestamp)}</td>
                      <td><ReadBadge unread={notification.unread} /></td>
                      <td><button type="button" className="notification-view-button" onClick={() => setSelectedNotification(notification)}>View</button></td>
                    </tr>
                  )) : null}
                  {!loading && !notifications.data.length ? <tr><td colSpan={7}><div className="notification-table-state">{apiAvailable ? "No notifications available." : "Notifications are unavailable right now."}</div></td></tr> : null}
                </TableBody>
              </Table>
            </div>
            {!loading && notifications.total > 0 ? <div className="notification-pagination"><TablePagination currentPage={notifications.currentPage} total={notifications.total} perPage={notifications.perPage} from={notifications.from} to={notifications.to} onChange={(page) => setCurrentPage(page)} /></div> : null}
          </section>

          <section className="notification-list-card reminder-list-card">
            <header className="notification-card-header"><h2>Reminder List</h2></header>
            <div className="notification-table-wrap">
              <Table>
                <TableHeader><tr><th>Reminder</th><th>Customer</th><th>Event</th><th>Due Date</th><th>Status</th><th>Related Automation Flow</th><th>Actions</th></tr></TableHeader>
                <TableBody>
                  {remindersLoading ? <tr><td colSpan={7}><div className="notification-table-state"><span className="spinner-border" /></div></td></tr> : null}
                  {!remindersLoading && reminders.data.length ? reminders.data.map((reminder) => (
                    <tr key={reminder.id}>
                      <td>{reminder.title || "Reminder"}</td><td>{reminder.customerName || "Not available"}</td><td>{reminder.eventName || "Not available"}</td>
                      <td>{formatDateTime(reminder.dueAt)}</td><td>{reminder.status || "Not available"}</td><td>{reminder.flowName || "Not available"}</td>
                      <td><button type="button" className="notification-view-button" disabled={!smsReminderActionsAvailable}>View</button></td>
                    </tr>
                  )) : null}
                  {!remindersLoading && !reminders.data.length ? <tr><td colSpan={7}><div className="reminder-empty-state"><span><FiCalendar /></span><strong>No reminders scheduled.</strong></div></td></tr> : null}
                </TableBody>
              </Table>
            </div>
            <p className="reminder-list-note">Reminder rows use automation flow reminder definitions until scheduled reminder instances are available.</p>
          </section>

        {selectedNotification ? (
          <div className="notification-drawer-backdrop" role="presentation" onClick={closeDetail}>
            <aside
              className="notification-detail-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Notification detail"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="notification-detail-header">
                <div>
                  <NotificationBadge type={selectedNotification.type} />
                  <h5 className="mt-2 mb-0">{selectedNotification.title || "Notification"}</h5>
                </div>
                <button type="button" className="close" aria-label="Close" onClick={closeDetail}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="notification-detail-body">
                <p>{selectedNotification.message || "No message available."}</p>
                <dl className="notification-detail-list">
                  <DetailRow label="Customer" value={selectedNotification.customerName} />
                  <DetailRow label="Event" value={selectedNotification.eventName} />
                  <DetailRow label="Conversation" value={selectedNotification.conversationId} />
                  <DetailRow label="Related Automation Flow" value={selectedNotification.flowName} />
                  <DetailRow label="Timestamp" value={formatDateTime(selectedNotification.timestamp)} />
                  <DetailRow label="Automation Action Executed" value={selectedNotification.automationAction} />
                  <DetailRow label="Status" value={selectedNotification.unread ? "Unread" : "Read"} />
                </dl>
              </div>
              <div className="notification-detail-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={closeDetail}>
                  Close
                </button>
              </div>
            </aside>
          </div>
        ) : null}
        </main>
      </Layout>
    </SmsAccessGuard>
  );
};

export default Notifications;
